import PropTypes from "prop-types";
import React, { useRef, useEffect, useState } from "react";
import { fileUpload } from "./CustomPlugins/fileUpload/plugin";
import { Editor } from "@tinymce/tinymce-react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { FormHelperText, Box } from "@mui/material";
import { noop } from "../../utils/helpers";
import clsx from "clsx";
import defaultPic1 from "../../assets/images/default_pic_1.png";
import { validateVideoUrl } from "../../utils/validation";
import i18n from "../../i18n";
import "./HtmlEditor.scss";
import { useTranslation } from "react-i18next";
import { getContentStyle } from "../../utils/htmlEditorUtils";

const useStyles = makeStyles(theme => ({
  editor: {
    position: "relative",
    margin: ({ disableMargin }) =>
      disableMargin ? theme.spacing(0) : theme.spacing(3, 0, 4, 0),
    height: ({ height }) => (height === "100%" ? height : "auto"),
    "& .tox": {
      "& .tox-button": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        "&.tox-button--naked": {
          color: theme.palette.primary.main,
          background: "none"
        }
      },
      "& .tox-toolbar__primary": {
        border: `solid 1px ${theme.palette.secondary[200]}`
      },
      "& .tox-tbtn svg": {
        fill: theme.palette.action.active
      }
    },
    "& .tox .tox-edit-area__iframe, .mce-content-body": {
      ...theme.typography.subtitle1,
      backgroundColor: ({ disabled }) =>
        !disabled
          ? theme.palette.inputBackground
          : theme.palette.action.disabled,
      borderBottom: ({ disableUnderline }) =>
        disableUnderline
          ? "none"
          : `2px solid ${theme.palette.action.disabled}`,
      opacity: ({ disabled }) => (disabled ? 0.38 : 1)
    }
  },
  editorError: {
    "& .tox .tox-edit-area__iframe": {
      borderBottomColor: `${theme.palette.error.main}`
    }
  },
  helperText: {
    margin: ({ inline }) => theme.spacing(inline ? 0.25 : -0.25, 1.75, 0, 1.5)
  },
  inlineToolbar: {
    position: "absolute",
    boxShadow:
      "0px 2px 10px rgba(0, 0, 0, 0.06), 0px 0px 4px rgba(0, 0, 0, 0.06)",
    top: -40,
    "& + .mce-content-body": {
      margin: 0
    }
  },
  characterLimitText: {
    margin: theme.spacing(-0.25, 1.5, 0, 1.5),
    textAlign: "end"
  }
}));

const HTMLEditor = ({
  value,
  onChange,
  onFileAdd,
  errorMessage,
  plugins,
  fileSizeLimit,
  menubar,
  disabled,
  toolbar,
  height,
  disableMargin,
  disableUnderline,
  disableResize,
  autoFocus,
  onDialogOpenChange,
  placeholder,
  inline,
  toolbarId,
  characterLimit,
  onNumCharactersChange,
  onBlur,
  readOnly
}) => {
  const classes = useStyles({
    disableMargin,
    disableUnderline,
    disabled,
    height,
    inline
  });
  const [currentNumCharacters, setCurrentNumCharacters] = useState(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const editorRef = useRef(null);
  let pluginsList = plugins.join(" ");

  const setup = editor => {
    fileUpload(editor);
    editor.ui.registry.addGroupToolbarButton("alignment", {
      icon: "align-left",
      tooltip: "Alignment",
      items: "alignleft aligncenter alignright | alignjustify"
    });
  };

  useEffect(() => {
    if (editorRef.current.editor) {
      if (onDialogOpenChange) {
        editorRef.current.editor.on("OpenWindow", editor =>
          onDialogOpenChange(true, editor)
        );
        editorRef.current.editor.on("CloseWindow", editor =>
          onDialogOpenChange(false, editor)
        );
      }
    }
  }, [editorRef]);

  useEffect(() => {
    if (characterLimit && editorRef.current && editorRef.current.editor) {
      const numCharacters = editorRef.current.editor.getContent({
        format: "text"
      }).length;
      setCurrentNumCharacters(numCharacters);
      onNumCharactersChange(numCharacters);
    }
  }, [value, editorRef.current]);

  // eslint-disable-next-line
  const fileNameValidateRegexpPattern = /^[\w\-]+.?[a-zA-Z0-9]+$/;

  const filenameValidate = filename => {
    return fileNameValidateRegexpPattern.test(filename);
  };

  const handleInit = (evt, editor) => {
    if (characterLimit && editorRef.current && editorRef.current.editor) {
      const numCharacters = editor.getContent({ format: "text" }).length;
      setCurrentNumCharacters(numCharacters);
      onNumCharactersChange(numCharacters);
    }
  };

  return (
    <div
      className={clsx(classes.editor, {
        [classes.editorError]: !!errorMessage
      })}
    >
      {/* This is a pretty hacky solution, but when the editor is inline and inside of a modal, the
      absolute positioning of the toolbar causes the toolbar to scroll instead of staying fixed to
      the top of the editor. To fix it, if the editor is inline, we pass in an ID for the toolbar and tinymce
      will insert the toolbar into the dom element below */}
      {inline && <div className={classes.inlineToolbar} id={toolbarId} />}
      <Editor
        value={value || ""}
        onInit={handleInit}
        init={{
          setup,
          placeholder,
          inline,
          height,
          resize: !disableResize,
          menubar: menubar,
          mobile: {
            theme: "silver"
          },
          plugins: pluginsList,
          toolbar,
          fixed_toolbar_container: `#${toolbarId}`,
          auto_focus: autoFocus,
          images_upload_handler: (blobInfo, success, failure) => {
            const src = URL.createObjectURL(blobInfo.blob());
            onFileAdd(blobInfo, src, "image");
            success(src);
          },
          media_url_resolver: async function(data, resolve, reject) {
            if (data.url && data.url.indexOf("blob:http") !== -1) {
              fetch(data.url)
                .then(r => r.blob())
                .then(blob => {
                  let filetype = blob.type.split("/")[0];
                  let embedHtml = "";

                  switch (filetype) {
                    case "video":
                      embedHtml =
                        '<video width="320" height="240" controls preload="metadata" loop >' +
                        '<source src="' +
                        data.url +
                        '" ' +
                        (blob.type ? ' type="' + blob.type + '"' : "") +
                        '">' +
                        "</video>";
                      break;
                    case "audio":
                      embedHtml =
                        "<audio controls>" +
                        '<source src="' +
                        data.url +
                        '" ' +
                        (blob.type ? ' type="' + blob.type + '"' : "") +
                        " />" +
                        "</audio>";
                      break;
                    default:
                      embedHtml = "";
                  }
                  return resolve({ html: embedHtml });
                })
                .catch(error => {
                  reject({ message: error });
                });
            } else {
              let embedHtml = "";
              let validateUrl = validateVideoUrl(data.url);
              if (validateUrl.type === "url") {
                embedHtml =
                  '<iframe src="' +
                  validateUrl.url +
                  '" width="320" height="240" ></iframe>';
              } else {
                embedHtml =
                  '<video width="320" height="240" src=' +
                  validateUrl.url +
                  ' controls preload="metadata" loop ></video>';
              }

              resolve({ html: embedHtml });
            }
          },
          file_picker_callback: function(callback, value, meta) {
            let input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/* audio/* video/*");
            input.onchange = function() {
              let file = this.files[0];

              let reader = new FileReader();
              reader.readAsDataURL(file);
              if (file.size / Math.pow(2, 20) > fileSizeLimit) {
                alert(
                  i18n.t("components.HTMLEditor.fileSizeLimitErrorMessage", {
                    fileSizeLimit: fileSizeLimit
                  })
                );
                return null;
              } else {
                if (filenameValidate(file.name)) {
                  reader.onload = function(e, filename = file.name) {
                    return fetch(e.target.result)
                      .then(async function(res) {
                        return {
                          blob: await res.blob(),
                          base64: res.url.split(",")[1],
                          filename: filename
                        };
                      })
                      .then(res => {
                        let uri = URL.createObjectURL(res.blob);
                        return {
                          blob: function() {
                            return res.blob;
                          },
                          uri: function() {
                            return uri;
                          },
                          base64: function() {
                            return res.base64;
                          },
                          filename: function() {
                            return res.filename;
                          }
                        };
                      })
                      .then(res => {
                        if (res) {
                          if (meta.filetype === "file") {
                            callback(res.uri(), { text: "Text for display" });
                            return onFileAdd(res, res.uri(), meta.filetype);
                          } else if (meta.filetype === "image") {
                            return callback(res.uri(), {
                              alt: "Alt text for image"
                            });
                          } else if (meta.filetype === "media") {
                            callback(res.uri(), { poster: defaultPic1 });
                            return onFileAdd(res, res.uri(), meta.filetype);
                          } else {
                            return null;
                          }
                        } else {
                          return null;
                        }
                      });
                  };
                } else {
                  alert(i18n.t("components.HTMLEditor.fileNameErrorMessage"));
                }
              }
            };

            input.click();
          },
          media_live_embeds: true,
          elementpath: false,
          content_css: [`${process.env.PUBLIC_URL}/md-bootstrap-gh.css`],
          content_style: `${getContentStyle(theme)}`,
          valid_children: "+span[div]",
          extended_valid_elements:
            "svg[*],path[*],img[*],a[*],link[*],head[*],script[*],button[*],span[*]"
        }}
        disabled={disabled || readOnly}
        ref={editorRef}
        onEditorChange={onChange}
        onFocusOut={onBlur}
        apiKey="sbvrndkc9icjjt0xljtgokclw8o17gamtny9entrhmrrq4l5"
      />
      <Box display="flex" justifyContent="space-between">
        <FormHelperText error={!!errorMessage} className={classes.helperText}>
          {errorMessage}
        </FormHelperText>
        {/**
         * Tinymce won't be able to get the current number of characters until the input is in focus
         * so we don't want to display the characters remaining until currentNumCharacters is not null. Otherwise,
         * it will show the wrong character count.
         */}
        {characterLimit && currentNumCharacters !== null && (
          <FormHelperText
            error={characterLimit - currentNumCharacters < 0}
            className={classes.characterLimitText}
          >
            {t("courseVersion.details.descriptionHelperText", {
              characters: characterLimit - currentNumCharacters,
              maxCharacters: characterLimit
            })}
          </FormHelperText>
        )}
      </Box>
    </div>
  );
};

HTMLEditor.propTypes = {
  /**
   * The error message to be displayed under the editor
   */
  errorMessage: PropTypes.string,
  onChange: PropTypes.func,
  onFileAdd: PropTypes.func,
  value: PropTypes.any,
  fileSizeLimit: PropTypes.number,
  menubar: PropTypes.bool,
  disabled: PropTypes.bool,
  plugins: PropTypes.arrayOf(
    PropTypes.oneOf([
      "lists",
      "link",
      "image",
      "media",
      "code",
      "advlist",
      "fileUpload",
      "textcolor",
      "fullpage"
    ])
  ),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disableMargin: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  disableResize: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onOpenDialog: PropTypes.func,
  placeholder: PropTypes.string,
  inline: PropTypes.bool,
  toolbarId: PropTypes.string, // required if inline is true
  characterLimit: PropTypes.number,
  readonly: PropTypes.bool
};

HTMLEditor.defaultProps = {
  readOnly: false,
  disabled: false,
  menubar: true,
  plugins: ["lists", "link", "image", "media", "code", "fileUpload"],
  onFileAdd: noop,
  fileSizeLimit: 500,
  toolbar:
    "bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link image media fileUpload code",
  height: 500
};

export default HTMLEditor;
